import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { Building2, Activity, MapPin, TrendingUp, DollarSign, Percent, PieChart } from 'lucide-react';
import RealEstateAppraisalModal from './realestate/RealEstateAppraisalModal';
import RealEstateLeaseModal from './realestate/RealEstateLeaseModal';
import RealEstateDisposeModal from './realestate/RealEstateDisposeModal';
import RealEstateOperationsPanel from './realestate/RealEstateOperationsPanel';

const Container = styled.div<{ $embedded?: boolean }>`
  padding: ${props => props.$embedded ? '1rem' : '2rem'};
  background: transparent;
  color: #e2e8f0;
`;

const Section = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'warning' | 'danger' }>`
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid;
  cursor: pointer;
  margin-right: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.15s;

  ${props => props.$variant === 'warning' && `
    background: rgba(245, 158, 11, 0.1);
    border-color: rgba(245, 158, 11, 0.4);
    color: #fbbf24;
    &:hover { background: rgba(245, 158, 11, 0.2); }
  `}
  ${props => props.$variant === 'danger' && `
    background: rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 0.4);
    color: #f87171;
    &:hover { background: rgba(239, 68, 68, 0.2); }
  `}
  ${props => (!props.$variant || props.$variant === 'primary') && `
    background: rgba(139, 92, 246, 0.1);
    border-color: rgba(139, 92, 246, 0.4);
    color: #a78bfa;
    &:hover { background: rgba(139, 92, 246, 0.2); }
  `}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

interface RealEstateEquityRow {
  id: number;                       // fact_re_equity_position id (silo)
  propertyName: string;
  propertyType: string;
  location: string;
  status: string;
  equityInvested: number;
  currentValuation: number;
  ownershipPct: number;
  capRate: number;                  // decimal e.g. 0.055
  currentNoi: number;
  occupancyPct: number;
  ltv: number;
  dscr: number;
  debtYield: number;
  acquisitionDate: string;
  valuationDate: string;
  appraisalFirm: string;
}

interface RealEstateDashboardProps {
  embedded?: boolean;
}

const RealEstateDashboard: React.FC<RealEstateDashboardProps> = ({ embedded = false }) => {
  const [positions, setPositions] = useState<RealEstateEquityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<any>({});
  const [appraiseTarget, setAppraiseTarget] = useState<RealEstateEquityRow | null>(null);
  const [leaseTarget, setLeaseTarget] = useState<RealEstateEquityRow | null>(null);
  const [disposeTarget, setDisposeTarget] = useState<RealEstateEquityRow | null>(null);
  const [opsTarget, setOpsTarget] = useState<RealEstateEquityRow | null>(null);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/credit/re-equity');
      const raw = Array.isArray(response.data) ? response.data : [];

      const rows: RealEstateEquityRow[] = raw.map((p: any) => ({
        id: p.id,
        propertyName: p.propertyName || 'Unnamed Property',
        propertyType: p.propertyType || '',
        location: p.location || '',
        status: p.status || 'ACTIVE',
        equityInvested: Number(p.equityInvested) || 0,
        currentValuation: Number(p.currentValuation) || 0,
        ownershipPct: Number(p.ownershipPct) || 0,
        capRate: Number(p.capRate) || 0,
        currentNoi: Number(p.currentNoi) || 0,
        occupancyPct: Number(p.occupancyPct) || 0,
        ltv: Number(p.ltv) || 0,
        dscr: Number(p.dscr) || 0,
        debtYield: Number(p.debtYield) || 0,
        acquisitionDate: p.acquisitionDate || '',
        valuationDate: p.valuationDate || '',
        appraisalFirm: p.appraisalFirm || ''
      }));

      setPositions(rows);

      const totalValue = rows.reduce((s, r) => s + r.currentValuation, 0);
      const totalEquity = rows.reduce((s, r) => s + r.equityInvested, 0);
      const totalNoi = rows.reduce((s, r) => s + r.currentNoi, 0);
      const avgLtv = rows.length ? rows.reduce((s, r) => s + r.ltv, 0) / rows.length : 0;
      const avgDscr = rows.length ? rows.reduce((s, r) => s + r.dscr, 0) / rows.length : 0;

      setSummary({
        totalValue,
        totalEquity,
        totalNoi,
        avgLtv,
        avgDscr,
        positionCount: rows.length
      });
    } catch (error) {
      console.error('Error loading RE equity dashboard:', error);
      setPositions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboardData(); }, [loadDashboardData]);

  const ActionsRenderer = (params: ICellRendererParams) => {
    const row = params.data as RealEstateEquityRow;
    const isDisposed = row.status === 'SOLD' || row.status === 'WRITTEN_OFF';
    return (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <ActionButton
          $variant="primary"
          disabled={isDisposed}
          onClick={() => setAppraiseTarget(row)}
          title="Record an appraisal / mark-to-market"
        >Appraise</ActionButton>
        <ActionButton
          $variant="warning"
          disabled={isDisposed}
          onClick={() => setLeaseTarget(row)}
          title="Record a new or renewed lease"
        >Lease</ActionButton>
        <ActionButton
          $variant="danger"
          disabled={isDisposed}
          onClick={() => setDisposeTarget(row)}
          title="Record a property disposition"
        >Dispose</ActionButton>
        <ActionButton
          $variant="primary"
          onClick={() => setOpsTarget(row)}
          title="Open property operations panel (rent roll, leases, KPIs, Argus, JV)"
        >Ops</ActionButton>
      </div>
    );
  };

  const columnDefs: ColDef[] = [
    { field: 'propertyName', headerName: 'Property', flex: 2, sortable: true, filter: true, cellStyle: { fontWeight: '600' } },
    { field: 'propertyType', headerName: 'Type', flex: 0.9, sortable: true, filter: true },
    { field: 'location', headerName: 'Location', flex: 1.4, sortable: true, filter: true },
    {
      field: 'status', headerName: 'Status', flex: 0.8, sortable: true, filter: true,
      cellStyle: (params: any) => ({
        fontWeight: 'bold',
        color: params.value === 'SOLD' ? '#94a3b8'
             : params.value === 'WRITTEN_OFF' ? '#f87171'
             : params.value === 'UNDER_CONTRACT' ? '#fbbf24'
             : '#34d399'
      })
    },
    {
      field: 'currentValuation', headerName: 'Valuation', flex: 1.2,
      valueFormatter: p => p.value ? `$${Number(p.value).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '',
      cellStyle: { textAlign: 'right', fontWeight: 'bold', color: '#60a5fa' }
    },
    {
      field: 'currentNoi', headerName: 'NOI', flex: 1,
      valueFormatter: p => p.value ? `$${Number(p.value).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : 'N/A',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'capRate', headerName: 'Cap Rate', flex: 0.8,
      // capRate stored as decimal in silo (0.055 = 5.5%)
      valueFormatter: p => p.value ? `${(Number(p.value) * 100).toFixed(2)}%` : 'N/A',
      cellStyle: { textAlign: 'right', fontFamily: 'monospace' }
    },
    {
      field: 'occupancyPct', headerName: 'Occupancy', flex: 0.8,
      // occupancy_pct stored as percentage in silo (94.0 = 94%) — do NOT multiply
      valueFormatter: p => p.value ? `${Number(p.value).toFixed(1)}%` : 'N/A',
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'ltv', headerName: 'LTV', flex: 0.7,
      // ltv stored as percentage in silo (60.0 = 60%) — do NOT multiply
      valueFormatter: p => p.value ? `${Number(p.value).toFixed(1)}%` : 'N/A',
      cellStyle: (p: any) => ({
        textAlign: 'right',
        color: Number(p.value) > 75 ? '#f87171' : '#e2e8f0',
        fontWeight: Number(p.value) > 75 ? 'bold' : 'normal'
      })
    },
    {
      field: 'dscr', headerName: 'DSCR', flex: 0.7,
      valueFormatter: p => p.value ? `${Number(p.value).toFixed(2)}x` : 'N/A',
      cellStyle: (p: any) => ({
        textAlign: 'right',
        color: Number(p.value) < 1.2 && Number(p.value) > 0 ? '#f87171' : '#34d399',
        fontWeight: 'bold'
      })
    },
    {
      headerName: 'Actions',
      flex: 2.2,
      sortable: false,
      filter: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { padding: '0 8px' }
    }
  ];

  if (loading) {
    return (
      <Container $embedded={embedded}>
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
          <div className="w-12 h-12 border-4 border-violet-500/20 border-t-violet-500 rounded-full animate-spin" />
          <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Loading Real Estate Properties...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container $embedded={embedded}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight mb-1 flex items-center gap-3">
            <Building2 className="w-6 h-6 text-violet-400" />
            Real Estate Dashboard
          </h1>
          <p className="text-slate-500 text-sm font-medium">Direct property ownership — appraisals, leases, dispositions</p>
        </div>
        <div className="flex items-center gap-3 px-4 py-2 bg-violet-500/10 border border-violet-500/20 rounded-xl">
          <Activity className="w-4 h-4 text-violet-400" />
          <span className="text-xs font-bold text-violet-400 uppercase tracking-widest">{summary.positionCount || 0} Properties</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="glass-card p-6 border-l-4 border-l-violet-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
            <DollarSign className="w-3 h-3" /> Total Valuation
          </div>
          <div className="text-2xl font-bold text-white">${(summary.totalValue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="glass-card p-6 border-l-4 border-l-blue-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
            <TrendingUp className="w-3 h-3" /> Equity Invested
          </div>
          <div className="text-2xl font-bold text-white">${(summary.totalEquity || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="glass-card p-6 border-l-4 border-l-emerald-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
            <Activity className="w-3 h-3" /> Aggregate NOI
          </div>
          <div className="text-2xl font-bold text-white">${(summary.totalNoi || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="glass-card p-6 border-l-4 border-l-amber-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
            <Percent className="w-3 h-3" /> Avg LTV
          </div>
          <div className="text-2xl font-bold text-white">{(summary.avgLtv || 0).toFixed(1)}%</div>
        </div>
      </div>

      <Section>
        <SectionTitle>
          <Building2 className="w-4 h-4 text-violet-400" />
          Property Positions
        </SectionTitle>
        <div className="ag-theme-alpine-dark" style={{ height: '600px', width: '100%', borderRadius: '12px', overflow: 'hidden' }}>
          <AgGridReact
            rowData={positions}
            columnDefs={columnDefs}
            defaultColDef={{
              resizable: true,
              sortable: true,
              filter: true,
              headerClass: 'text-[10px] font-bold uppercase tracking-widest text-slate-500'
            }}
            pagination={true}
            paginationPageSize={20}
          />
        </div>
      </Section>

      {opsTarget ? (
        <RealEstateOperationsPanel
          property={{
            id: opsTarget.id,
            propertyName: opsTarget.propertyName,
            propertyType: opsTarget.propertyType,
            location: opsTarget.location
          }}
          onClose={() => setOpsTarget(null)}
        />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Section>
            <SectionTitle>
              <PieChart className="w-4 h-4 text-blue-400" />
              Geographic Concentration
            </SectionTitle>
            <div className="flex flex-col items-center justify-center h-48 text-slate-600">
              <MapPin className="w-12 h-12 mb-4 opacity-20" />
              <p className="text-sm italic">Click "Ops" on any property row to open the operations panel — rent roll, leases, T-12 KPIs, Argus DCF, and JV terms.</p>
            </div>
          </Section>

          <Section>
            <SectionTitle>
              <Activity className="w-4 h-4 text-violet-400" />
              Leverage Analysis
            </SectionTitle>
            <div className="flex flex-col items-center justify-center h-48 text-slate-600">
              <TrendingUp className="w-12 h-12 mb-4 opacity-20" />
              <p className="text-sm italic">Loan-to-value and debt-service ratio trends</p>
            </div>
          </Section>
        </div>
      )}

      {appraiseTarget && (
        <RealEstateAppraisalModal
          position={appraiseTarget}
          onClose={() => setAppraiseTarget(null)}
          onSuccess={() => { setAppraiseTarget(null); loadDashboardData(); }}
        />
      )}
      {leaseTarget && (
        <RealEstateLeaseModal
          position={leaseTarget}
          onClose={() => setLeaseTarget(null)}
          onSuccess={() => { setLeaseTarget(null); loadDashboardData(); }}
        />
      )}
      {disposeTarget && (
        <RealEstateDisposeModal
          position={disposeTarget}
          onClose={() => setDisposeTarget(null)}
          onSuccess={() => { setDisposeTarget(null); loadDashboardData(); }}
        />
      )}
    </Container>
  );
};

export default RealEstateDashboard;
